import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Mail, MessageSquare, ArrowLeft } from "lucide-react";

export const ForgotPasswordDialog = ({ open, onOpenChange }) => {
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [channel, setChannel] = useState("email");
  const [code, setCode] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setStep(1); setIdentifier(""); setChannel("email"); setCode(""); setNext(""); setConfirm(""); setError(""); };

  const sendCode = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { identifier, channel });
      toast.success(t("forgot.codeSent"));
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) { setError(t("forgot.tooShort")); return; }
    if (next !== confirm) { setError(t("forgot.mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { identifier, channel, code: code.trim(), new_password: next });
      toast.success(t("forgot.success"));
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>{t("forgot.title")}</DialogTitle>
          <DialogDescription>{step === 1 ? t("forgot.subtitle") : t("forgot.codeHint")}</DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={sendCode} className="space-y-4">
            <div className="space-y-2">
              <Label>{t("forgot.channel")}</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="forgot-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email"><span className="flex items-center gap-2"><Mail className="h-4 w-4" /> {t("forgot.email")}</span></SelectItem>
                  <SelectItem value="sms"><span className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> {t("forgot.sms")}</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{channel === "email" ? t("forgot.emailAddr") : t("forgot.phone")}</Label>
              <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} data-testid="forgot-identifier" dir="ltr" required />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="forgot-send-code"
              className="brand-btn flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-medium transition-opacity hover:opacity-90 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("forgot.sendCode")}
            </button>
          </form>
        ) : (
          <form onSubmit={doReset} className="space-y-4">
            <div className="space-y-2">
              <Label>{t("forgot.code")}</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="forgot-code" dir="ltr" inputMode="numeric" maxLength={6} required />
            </div>
            <div className="space-y-2">
              <Label>{t("forgot.newPassword")}</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="forgot-new-password" autoComplete="new-password" required />
            </div>
            <div className="space-y-2">
              <Label>{t("forgot.confirm")}</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="forgot-confirm-password" autoComplete="new-password" required />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="forgot-reset-submit"
              className="brand-btn flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-medium transition-opacity hover:opacity-90 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("forgot.reset")}
            </button>
            <button type="button" onClick={() => { setStep(1); setError(""); }} data-testid="forgot-back"
              className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> {t("forgot.back")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
